import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Tabs, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import DrawerModal from '../../shared/DrawerModal';
import { closeModal, getIsVisibleModal } from '../../../redux/store/ui/common';
import {
  selectSelectedClaim,
  addClaimNote,
  uploadClaimDocument,
} from '../../../redux/store/userData/claims';
import { colors } from '../../../theme/theme';
import styles from './ClaimTimelineAddModal.styles';

const { TabPane } = Tabs;
const { Dragger } = Upload;
const { TextArea } = Input;

const ClaimTimelineAddModal: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const visible = useSelector(getIsVisibleModal('ClaimTimelineAddModal'));
  const selectedClaim = useSelector(selectSelectedClaim) as any;

  const [activeTab, setActiveTab] = useState<string>('note');
  const [note, setNote] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      setActiveTab('note');
      setNote('');
      setComment('');
      setFileList([]);
      setSubmitting(false);
    }
  }, [visible]);

  const handleClose = () => {
    dispatch(closeModal('ClaimTimelineAddModal'));
  };

  const handleSubmitNote = async () => {
    const trimmed = note.trim();
    if (!trimmed || !selectedClaim?.id) return;
    setSubmitting(true);
    await dispatch(addClaimNote(selectedClaim.id, trimmed));
    setSubmitting(false);
    handleClose();
  };

  const handleSubmitDoc = () => {
    if (fileList.length === 0 || !selectedClaim?.id) return;
    const file = fileList[0].originFileObj;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setSubmitting(true);
      await dispatch(
        uploadClaimDocument(
          selectedClaim.id,
          reader.result as string,
          comment.trim()
        )
      );
      setSubmitting(false);
      handleClose();
    };
    reader.readAsDataURL(file);
  };

  const uploadProps = {
    name: 'file',
    accept: 'image/*,.pdf',
    fileList,
    multiple: false,
    customRequest: ({ onSuccess }: any) => {
      onSuccess();
    },
    showUploadList: false,
    onChange(info: any) {
      setFileList(info.fileList.filter((f: any) => !!f.status).slice(-1));
    },
  };

  const selectedFile = fileList[0];
  const removeSelectedFile = () => setFileList([]);

  const noteDisabled = !note.trim() || submitting;
  const docDisabled = fileList.length === 0 || submitting;

  return (
    <>
      <DrawerModal visible={visible} onClose={handleClose}>
        <div
          data-testid="ClaimTimelineAddModal-contents"
          className="claim-timeline-add-contents"
        >
          <div className="claim-timeline-add-title">
            {intl.formatMessage({ id: 'fleetadmin.claim.timeline.add' })}
          </div>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane
              tab={intl.formatMessage({
                id: 'fleetadmin.claim.timeline.tab.note',
              })}
              key="note"
            >
              <p className="label">
                {intl.formatMessage({
                  id: 'fleetadmin.claim.timeline.note.label',
                })}
              </p>
              <textarea
                data-testid="ClaimTimelineAddModal-noteInput"
                className="comment-area"
                placeholder={intl.formatMessage({
                  id: 'fleetadmin.claim.timeline.note.placeholder',
                })}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                data-testid="ClaimTimelineAddModal-noteSubmitBtn"
                className="submit-button"
                onClick={handleSubmitNote}
                disabled={noteDisabled}
                loading={submitting}
                style={
                  !note.trim() ? { background: colors.buttonDisabled } : {}
                }
              >
                {intl.formatMessage({ id: 'submit' })}
              </Button>
              <Button className="cancel-button" onClick={handleClose}>
                {intl.formatMessage({ id: 'cancel' })}
              </Button>
            </TabPane>
            <TabPane
              tab={intl.formatMessage({
                id: 'fleetadmin.claim.timeline.tab.doc',
              })}
              key="doc"
            >
              <Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {intl.formatMessage({
                    id: 'fleetadmin.claim.timeline.doc.title',
                  })}
                </p>
                <p className="ant-upload-hint">
                  {intl.formatMessage({
                    id: 'fleetadmin.claim.timeline.doc.description',
                  })}
                </p>
              </Dragger>
              {selectedFile && (
                <div className="claim-timeline-file-row">
                  <span className="claim-timeline-file-name">
                    📎 {selectedFile.name}
                  </span>
                  <span
                    className="claim-timeline-file-remove"
                    onClick={removeSelectedFile}
                  >
                    ✕
                  </span>
                </div>
              )}
              <p className="label doc-comment-label">
                {intl.formatMessage({
                  id: 'fleetadmin.claim.timeline.doc.comment',
                })}
              </p>
              <TextArea
                data-testid="ClaimTimelineAddModal-commentInput"
                value={comment}
                rows={2}
                onChange={(e) => setComment(e.target.value)}
              />
              <Button
                data-testid="ClaimTimelineAddModal-docSubmitBtn"
                className="submit-button"
                onClick={handleSubmitDoc}
                disabled={docDisabled}
                loading={submitting}
                style={
                  fileList.length === 0
                    ? { background: colors.buttonDisabled }
                    : {}
                }
              >
                {intl.formatMessage({ id: 'submit' })}
              </Button>
              <Button className="cancel-button" onClick={handleClose}>
                {intl.formatMessage({ id: 'cancel' })}
              </Button>
            </TabPane>
          </Tabs>
        </div>
      </DrawerModal>
      <style jsx global>
        {styles}
      </style>
    </>
  );
};

export default ClaimTimelineAddModal;
